import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { CartItem, DeliveryRule } from "../../../common/types/interfaces";
import { DeliveryType } from "../../../common/types/enums";
import { selectClusterList } from "../../../store/slices/clusterListSlice";
import { getApi } from "../../../api/api";
import { isPointInPolygon } from "../../../common/utils/functions/isPointInPolygon";

interface UseDeliveryCalculatorProps {
  cartItems: CartItem[];
  deliveryAddress?: {
    latitude: number;
    longitude: number;
  } | null;
  deliveryType: DeliveryType;
  clusterId: number;
}

export const useDeliveryCalculator = ({
  cartItems,
  deliveryAddress,
  deliveryType,
  clusterId,
}: UseDeliveryCalculatorProps) => {
  const api = getApi();
  const clusters = useSelector(selectClusterList);
  const currentCluster = clusters.find((c) => Number(c.id) === clusterId);

  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(false);
  const [deliveryStoreId, setDeliveryStoreId] = useState<number | null>(null);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [applicableRules, setApplicableRules] = useState<DeliveryRule[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Сторы кластера
  useEffect(() => {
    let cancelled = false;

    const loadStores = async () => {
      try {
        const list = await api.getStores({ clusterId, target: "logmall" });
        if (!cancelled) setStores(list || []);
      } catch {
        if (!cancelled) setStores([]);
      }
    };

    if (clusterId) loadStores();

    return () => {
      cancelled = true;
    };
  }, [api, clusterId]);

  const cartItemsKey = useMemo(
    () => cartItems.map((item) => `${item.id}:${item.quantity}`).join("|"),
    [cartItems],
  );

  const deliveryAddressKey = useMemo(() => {
    if (!deliveryAddress) return null;
    return `${deliveryAddress.latitude},${deliveryAddress.longitude}`;
  }, [deliveryAddress?.latitude, deliveryAddress?.longitude]);

  const storesKey = useMemo(() => stores.map((s) => s.id).join(","), [stores]);

  const clusterDeliveryKey = useMemo(() => {
    if (!currentCluster?.delivery) return "";
    return JSON.stringify(currentCluster.delivery);
  }, [currentCluster?.delivery]);

  useEffect(() => {
    const calculateDelivery = async () => {
      setAvailable(false);
      setDeliveryStoreId(null);
      setDeliveryDistance(null);
      setDeliveryFee(0);
      setApplicableRules([]);
      setError(null);

      if (
        deliveryType !== DeliveryType.DELIVERY ||
        !deliveryAddress ||
        !currentCluster
      ) {
        return;
      }

      setLoading(true);

      try {
        const storesWithDelivery = stores.filter((s) => s.withDelivery);

        if (!storesWithDelivery.length) {
          setError("Нет точек с доставкой");
          return;
        }

        const addressPoint = {
          lat: Number(deliveryAddress.latitude),
          lng: Number(deliveryAddress.longitude),
        };

        let nearestStore: any = null;
        let minDistance = Infinity;

        for (const store of storesWithDelivery) {
          if (!store.address?.latitude || !store.address?.longitude) continue;

          const storeZones = store.deliveryZones || [];
          let inZone = false;
          let matchedZoneIds: number[] = [];

          if (storeZones.length > 0) {
            for (const zone of storeZones) {
              if (isPointInPolygon(addressPoint, zone.polygon)) {
                inZone = true;
                matchedZoneIds.push(zone.clusterDeliveryId);
              }
            }
          } else {
            const distance = calculateDistance(
              Number(store.address.latitude),
              Number(store.address.longitude),
              addressPoint.lat,
              addressPoint.lng,
            );
            if (isNaN(distance)) continue;

            const distanceMeters = distance * 1000;
            const withinStoreRadius =
              store.deliveryRadius === null ||
              distanceMeters <= Number(store.deliveryRadius);

            if (withinStoreRadius) inZone = true;
          }

          if (!inZone) continue;

          const distance = calculateDistance(
            Number(store.address.latitude),
            Number(store.address.longitude),
            addressPoint.lat,
            addressPoint.lng,
          );

          if (distance < minDistance) {
            minDistance = distance;
            nearestStore = { ...store, distance, matchedZoneIds };
          }
        }

        if (!nearestStore) {
          setError("Доставка недоступна по этому адресу");
          return;
        }

        const itemsTotal = cartItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        );

        const store = nearestStore;
        const distanceMeters = store.distance * 1000;
        const allRules = (currentCluster.delivery as DeliveryRule[]) || [];
        const hasZones = (store.deliveryZones || []).length > 0;

        let filteredRules: DeliveryRule[];

        if (hasZones) {
          filteredRules = allRules.filter((r) =>
            (store.matchedZoneIds || []).includes(Number((r as any).id)),
          );
        } else {
          filteredRules = allRules.filter(
            (r) => Number(r.radius) >= distanceMeters,
          );
        }

        const applicable = filteredRules
          .filter((r) => Number(r.level) <= itemsTotal)
          .sort((a, b) => Number(a.level) - Number(b.level));

        const allApplicable = filteredRules.sort(
          (a, b) => Number(a.level) - Number(b.level),
        );

        let price = 0;
        if (applicable.length) {
          price = Math.min(...applicable.map((r) => Number(r.value)));
        }

        setAvailable(true);
        setDeliveryStoreId(store.id ?? null);
        setDeliveryDistance(store.distance);
        setDeliveryFee(price);
        setApplicableRules(allApplicable);
      } catch {
        setError("Ошибка расчета доставки");
      } finally {
        setLoading(false);
      }
    };

    calculateDelivery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliveryType,
    deliveryAddressKey,
    storesKey,
    clusterDeliveryKey,
    cartItemsKey,
  ]);

  return {
    available,
    deliveryStoreId,
    deliveryDistance,
    deliveryFee,
    applicableRules,
    loading,
    error,
  };
};

const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
